/**
 * CollateralSection — multi-collateral wizard section.
 *
 * No "Has Collateral" toggle: the Add Collateral button is always
 * visible, and has_collateral is derived from whether the list is
 * non-empty. This keeps the UX honest — a contract is collateralised
 * when there's at least one provider on it, no separate flag needed.
 *
 * The legacy single-collateral inline fields on contract_master are
 * kept in sync with the first row of the list so PDF / reports that
 * still read them continue to work until they're migrated.
 */

import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useContractFormContext } from '../ContractFormContext';
import ContractCollateralsSection from '../../ContractCollateralsSection';

function CollateralSection() {
  const { watch, setValue } = useFormContext();
  const {
    refData,
    appendToRefData,
    contractCollaterals,
    setContractCollaterals,
    isEditable,
  } = useContractFormContext();

  const investmentCurrencyId = watch('investment_currency_id');
  const selectedCurrency = (refData.currencies || []).find(c => c.id === investmentCurrencyId);
  const currencyCode = selectedCurrency?.currency_code || 'ZAR';

  // Sync has_collateral + the legacy single-collateral form fields with
  // the first row of the list. has_collateral is now derived from the
  // list — the user no longer toggles it directly.
  useEffect(() => {
    const first = contractCollaterals[0];
    if (first) {
      setValue('has_collateral', true);
      setValue('collateral_provider_id', first.collateral_provider_id || '');
      setValue('collateral_type_id', first.collateral_type_id || '');
      setValue('collateral_amount', first.collateral_amount ?? '');
      setValue('collateral_multiplier', first.collateral_multiplier ?? '');
      setValue('nominal_amount', first.nominal_amount ?? '');
      setValue('collateral_in_favour_of', first.collateral_in_favour_of || '');
      setValue('collateral_description', first.collateral_description || '');
      setValue('contingent_liability', first.contingent_liability ?? '');
    } else {
      setValue('has_collateral', false);
      setValue('collateral_provider_id', '');
      setValue('collateral_type_id', '');
      setValue('collateral_amount', '');
      setValue('collateral_multiplier', '');
      setValue('nominal_amount', '');
      setValue('collateral_in_favour_of', '');
      setValue('collateral_description', '');
      setValue('contingent_liability', '');
    }
  }, [contractCollaterals, setValue]);

  const handleAddCollateral = (payload) => {
    setContractCollaterals(prev => [...prev, { ...payload }]);
  };

  const handleUpdateCollateral = (rowId, payload) => {
    setContractCollaterals(prev => prev.map(row => {
      const matches = row.id !== undefined ? row.id === rowId : row.collateral_provider_id === payload.collateral_provider_id;
      return matches ? { ...row, ...payload } : row;
    }));
  };

  const handleRemoveCollateral = (row) => {
    setContractCollaterals(prev => prev.filter(r => {
      if (row.id !== undefined && r.id !== undefined) return r.id !== row.id;
      return r.collateral_provider_id !== row.collateral_provider_id;
    }));
  };

  const handleProviderCreated = (provider) => {
    appendToRefData('collateralProviders', provider);
  };

  const disabled = isEditable === false;

  return (
    <ContractCollateralsSection
      collaterals={contractCollaterals}
      availableProviders={refData.collateralProviders || []}
      collateralTypes={refData.collateralTypes || []}
      currencies={refData.currencies || []}
      defaultCurrencyId={investmentCurrencyId || null}
      onAddCollateral={handleAddCollateral}
      onUpdateCollateral={handleUpdateCollateral}
      onRemoveCollateral={handleRemoveCollateral}
      onProviderCreated={handleProviderCreated}
      disabled={disabled}
      currencyCode={currencyCode}
      countries={refData.currencies || []}
    />
  );
}

export default CollateralSection;
